import { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2,
  Users,
  CheckCircle,
  Clock,
  TrendingUp,
  Calendar,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const statusColor = (status) => {
  const map = {
    active: "bg-emerald-500/20 text-emerald-400",
    pending: "bg-amber-500/20   text-amber-400",
    trial: "bg-blue-500/20    text-blue-400",
    cancelled: "bg-red-500/20     text-red-400",
    past_due: "bg-orange-500/20  text-orange-400",
  };
  return map[status] ?? "bg-slate-500/20 text-slate-400";
};

const todayLabel = () =>
  new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({
  title,
  value,
  icon: Icon,
  trend,
  trendLabel,
  color,
  testId,
}) => {
  const iconColor = {
    teal: "bg-teal-500/20    text-teal-400",
    blue: "bg-blue-500/20    text-blue-400",
    emerald: "bg-emerald-500/20 text-emerald-400",
    amber: "bg-amber-500/20   text-amber-400",
  };

  return (
    <div
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColor[color]}`}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex flex-col items-end gap-1">
          {trend !== undefined && (
            <span className="flex items-center gap-1 text-emerald-400 text-sm">
              <TrendingUp className="w-4 h-4" />+{trend}
            </span>
          )}
          <span className="text-[10px] text-slate-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {todayLabel()}
          </span>
        </div>
      </div>
      <p
        className="text-3xl font-bold text-slate-900 dark:text-white mb-1"
        data-testid={`${testId}-value`}
      >
        {value}
      </p>
      <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
      {trendLabel && (
        <p className="text-xs text-slate-400 mt-1">{trendLabel}</p>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, companiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`),
        axios.get(`${BACKEND_URL}/api/admin/companies`),
      ]);
      if (statsRes.data.success) setStats(statsRes.data.stats);
      if (companiesRes.data.success) setCompanies(companiesRes.data.companies);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-6 p-6" data-testid="admin-dashboard-tab">
      {/* ── Stat Cards ── */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="admin-stats-grid"
      >
        <StatCard
          title="Total Companies"
          value={stats?.total_companies ?? 0}
          icon={Building2}
          trend={stats?.new_companies_this_month}
          trendLabel="new this month"
          color="teal"
          testId="stat-total-companies"
        />
        <StatCard
          title="Total Users"
          value={stats?.total_users ?? 0}
          icon={Users}
          color="blue"
          testId="stat-total-users"
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.companies_by_status?.active ?? 0}
          icon={CheckCircle}
          color="emerald"
          testId="stat-active-subscriptions"
        />
        <StatCard
          title="Pending Activations"
          value={stats?.companies_by_status?.pending ?? 0}
          icon={Clock}
          color="amber"
          testId="stat-pending-activations"
        />
      </div>

      {/* ── Charts Row ── */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Companies by Type */}
        <div
          className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6"
          data-testid="companies-by-type-chart"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Companies by Type
            </h3>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {todayLabel()}
            </span>
          </div>
          <div className="space-y-3">
            {Object.entries(stats?.companies_by_type ?? {}).map(
              ([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-300">
                    {type}
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-500 rounded-full"
                        style={{
                          width: `${(count / (stats?.total_companies || 1)) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-slate-900 dark:text-white font-medium w-8 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        {/* Module Subscriptions */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Module Subscriptions
            </h3>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {todayLabel()}
            </span>
          </div>
          <div className="space-y-3">
            {stats?.module_subscriptions?.slice(0, 6).map((mod) => (
              <div
                key={mod.module_code}
                className="flex items-center justify-between"
              >
                <span className="text-slate-600 dark:text-slate-300 text-sm">
                  {mod.module_name}
                </span>
                <span className="px-2 py-1 bg-teal-500/20 text-teal-400 text-xs font-medium rounded">
                  {mod.subscriptions} active
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Recent Companies ── */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Recent Companies
            </h3>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {todayLabel()}
            </span>
          </div>
          <button
            onClick={() => navigate("/admin/companies")}
            className="text-sm text-teal-400 hover:text-teal-300 flex items-center gap-1"
          >
            View All <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {companies.slice(0, 5).map((company) => (
            <div
              key={company.id}
              className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/30"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <p className="text-slate-900 dark:text-white font-medium">
                    {company.company_name}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {company.company_type} • {company.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-slate-400">
                  {company.created_at
                    ? new Date(company.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    : "N/A"}
                </span>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${statusColor(company.subscription_status)}`}
                >
                  {company.subscription_status}
                </span>
                <span className="text-sm text-slate-400">
                  {company.module_count ?? 0} modules
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
